<template>
  <div style="display: flex; flex-direction: column; background: #F8F8F9;">
    <div class="cm-toolbar">
      <h2>Complex Care Operations</h2>

      <div class="cm-stepper">
        <button class="cm-icon-btn" @click="shiftRange(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" /></button>
        <input class="cm-input cm-input-sm" type="date" v-model="dateFrom" @change="onCustomDate" style="width: 108px; text-align: center;" />
        <span style="color: #A5A5A6; font-size: 11px;">→</span>
        <input class="cm-input cm-input-sm" type="date" v-model="dateTo" @change="onCustomDate" style="width: 108px; text-align: center;" />
        <button class="cm-icon-btn" @click="shiftRange(1)" :disabled="isToday"><FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" /></button>
      </div>

      <div class="cm-seg">
        <button
          v-for="preset in rangePresets"
          :key="preset.value"
          :class="{ on: activePreset === preset.value }"
          @click="applyPreset(preset.value)"
        >{{ preset.label }}</button>
      </div>

      <div class="cm-seg" style="margin-left: 12px;">
        <button
          v-for="t in tabs"
          :key="t.id"
          :class="{ on: activeTab === t.id }"
          @click="selectTab(t.id)"
        >{{ t.label }}</button>
      </div>

      <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
        <button class="cm-icon-btn" @click="fetchSummary" :disabled="loading" title="Refresh">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
        </button>
      </div>
    </div>

    <div style="padding: 10px 16px 0;">
      <Worklist_FilterBar
        storage-key="complex_care_operations_filters"
        :demographic-filters="[]"
        @filter-changed="setEntityFilters"
      />
    </div>

    <div style="display: flex; align-items: center; gap: 8px; padding: 10px 16px 8px; flex-wrap: wrap;">
      <div style="min-width: 190px; max-width: 260px;">
        <SimpleMultiSelect
          :model-value="selectedMetricIds"
          :options="metricOptions"
          :sensitive-labels="false"
          placeholder="All programs"
          @update:model-value="setPrograms"
        />
      </div>
      <div style="min-width: 170px; max-width: 240px;">
        <SimpleMultiSelect
          :model-value="managerIds"
          :options="managerOptions"
          :placeholder="options.can_view_all ? 'All team leaders' : 'My team'"
          sensitive-labels
          @update:model-value="setManagers"
        />
      </div>
      <div style="min-width: 170px; max-width: 240px;">
        <SimpleMultiSelect
          :model-value="selectedMemberIds"
          :options="memberOptions"
          placeholder="All team members"
          searchable
          sensitive-labels
          @update:model-value="setMembers"
        />
      </div>
      <button
        v-if="metricTouched || memberTouched || managerIds.length"
        style="font-size: 11px; color: #6B7280; text-decoration: underline; background: none; border: none; cursor: pointer; padding: 4px 6px;"
        @click="resetFilters"
      >Reset filters</button>
    </div>

    <div style="padding: 6px 16px 20px; display: flex; flex-direction: column; gap: 14px;">
      <div v-if="error" class="cm-panel" style="padding: 12px 16px; color: #B42318; background: #FEF3F2; border-color: #FECDCA; font-size: 11.5px;">
        {{ error }}
      </div>

      <div v-if="loading" style="padding: 40px; text-align: center; color: #8B8B8D; font-size: 11.5px;">
        <div class="cm-spinner" style="margin: 0 auto 10px;"></div>
        Loading data...
      </div>

      <template v-else-if="summary && summary.rows && summary.rows.length">
        <div v-if="unavailableColumns.length" class="cm-panel" style="padding: 8px 12px; background: #FAFAFA;">
          <div v-for="c in unavailableColumns" :key="c.key" style="font-size: 10.5px; color: #8B8B8D; line-height: 1.5;">
            <strong style="color: #6B7280;">{{ c.label }}:</strong> {{ c.reason }}
          </div>
        </div>

        <div class="cm-panel">
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-bottom: 1px solid #E8E9E9;">
            <div style="font-size: 11.5px; font-weight: 600; color: #353537;">
              {{ activeTab === 'activity' ? 'Report one: activity and productivity' : 'Report two: outcomes and interactions' }}
            </div>
            <div style="display: flex; align-items: center; gap: 12px;">
              <span v-if="summary.scope && summary.scope.basis" style="font-size: 10.5px; color: #A5A5A6;">
                {{ summary.scope.basis }}
              </span>
              <ExportMenu
                :disabled="exporting || !summary.rows.length"
                :exports="[
                  { type: 'csv', action: () => exportReport('summary', 'csv') },
                  { type: 'excel', action: () => exportReport('summary', 'xlsx') },
                ]"
              />
            </div>
          </div>

          <div style="max-height: 480px; overflow: auto;">
            <table class="cm-table">
              <thead>
                <tr>
                  <th :style="headerStyle('left')" @click="sortBy('label')">
                    Team member{{ sortMark('label') }}
                  </th>
                  <th
                    v-for="col in summary.columns"
                    :key="col.key"
                    :style="headerStyle('right', col.availability === 'unavailable')"
                    @click="col.availability !== 'unavailable' && sortBy(col.key)"
                  >
                    <OperationsColumnHeader :label="col.label" :definition="col.definition" :reason="col.reason || ''">{{ sortMark(col.key) }}</OperationsColumnHeader>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in sortedRows" :key="row.id" style="border-top: 1px solid #F0F0F1;">
                  <td style="padding: 6px 10px; font-size: 11.5px; color: #353537; font-weight: 500;" class="sensitive" data-spoof-kind="person">
                    {{ row.label }}
                  </td>
                  <td
                    v-for="col in summary.columns"
                    :key="col.key"
                    style="padding: 6px 10px; font-size: 11.5px; text-align: right; font-variant-numeric: tabular-nums;"
                  >
                    <span v-if="col.availability === 'unavailable'" style="color: #A5A5A6;" :title="col.reason || 'Unavailable'">—</span>
                    <button
                      v-else-if="cellOf(row, col).drillable && cellOf(row, col).count"
                      type="button"
                      style="background: none; border: none; padding: 0; color: #0284C7; font-weight: 600; cursor: pointer; text-decoration: underline; font-family: inherit; font-size: inherit;"
                      @click="openDrill(row, col)"
                    >{{ formatCell(cellOf(row, col), col) }}</button>
                    <span v-else style="color: #353537;">{{ formatCell(cellOf(row, col), col) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="drill.active" class="cm-panel" style="margin-top: 4px;">
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-bottom: 1px solid #E8E9E9;">
            <div>
              <span style="font-size: 12px; font-weight: 600; color: #353537;"><span class="sensitive" data-spoof-kind="person">{{ drill.rowLabel }}</span> · {{ drill.colLabel }}</span>
              <span v-if="!drill.loading" style="font-size: 11px; color: #6B7280; margin-left: 8px;">({{ drill.total.toLocaleString() }} records)</span>
              <div v-if="drill.definition" style="font-size: 10.5px; color: #8B8B8D; margin-top: 2px;">{{ drill.definition }}</div>
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <ExportMenu
                :disabled="exporting || drill.loading || !drill.rows.length"
                :exports="[
                  { type: 'csv', action: () => exportReport('drill', 'csv') },
                  { type: 'excel', action: () => exportReport('drill', 'xlsx') },
                ]"
              />
              <button class="cm-icon-btn" @click="closeDrill" title="Close drilldown">
                <FontAwesomeIcon :icon="['fas', 'times']" style="font-size: 11px;" />
              </button>
            </div>
          </div>

          <div v-if="drill.loading" style="padding: 24px; text-align: center; color: #8B8B8D; font-size: 11.5px;">
            <div class="cm-spinner" style="margin: 0 auto 8px;"></div>
            Loading evidence rows...
          </div>
          <div v-else-if="!drill.rows.length" style="padding: 24px; text-align: center; color: #8B8B8D; font-size: 11.5px;">
            No evidence rows for this selection.
          </div>
          <div v-else style="max-height: 440px; overflow: auto;">
            <table class="cm-table">
              <thead>
                <tr>
                  <th
                    v-for="c in drill.columns"
                    :key="c.key"
                    :style="headerStyle(c.format === 'number' ? 'right' : 'left')"
                    @click="sortDrill(c.key)"
                  >{{ c.label }}{{ drillSortMark(c.key) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(r, ri) in drill.rows"
                  :key="ri"
                  style="border-top: 1px solid #F0F0F1; cursor: pointer;"
                  @click="openPatient(r)"
                >
                  <td
                    v-for="c in drill.columns"
                    :key="c.key"
                    style="padding: 6px 10px; font-size: 11.5px; white-space: nowrap;"
                    :style="{ textAlign: c.format === 'number' ? 'right' : 'left' }"
                    :class="{ sensitive: c.sensitive }"
                  >
                    <span v-if="c.key === 'patient_name'" class="sensitive" style="color: #0284C7; font-weight: 500;">{{ r[c.key] || '—' }}</span>
                    <span v-else-if="c.key === 'patient_identifier'" class="sensitive" style="color: #6B7280;">{{ r[c.key] || '—' }}</span>
                    <span v-else style="color: #353537;">{{ formatDrillVal(r[c.key], c) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!drill.loading && drill.total > drill.rows.length" style="padding: 8px 12px; display: flex; justify-content: center; border-top: 1px solid #F0F0F1;">
            <button class="cm-icon-btn" style="width: auto; padding: 0 10px; font-size: 11px;" @click="loadMoreDrill">
              Show more ({{ drill.rows.length.toLocaleString() }} of {{ drill.total.toLocaleString() }})
            </button>
          </div>
        </div>
      </template>

      <div v-else style="padding: 40px; text-align: center; color: #A5A5A6; font-size: 11.5px;">
        No Complex Care cases or activity found for this scope and date range.
      </div>
    </div>

    <div v-if="drawer.open" style="position: fixed; inset: 0; z-index: 60; display: flex; justify-content: flex-end; background: rgba(38,38,40,.28);"
         @click.self="closeDrawer">
      <div style="width: 60%; max-width: 1100px; background: #FFFFFF; height: 100%; overflow: auto; box-shadow: -4px 0 24px rgba(38,38,40,.18);">
        <Patient_Drawer
          :patient-identifier="drawer.patientIdentifier"
          :entity-id="drawer.entityId"
          :patient-name="drawer.patientName"
          :enabled-tabs="['details','programs','engagement','care_graph','utilization']"
          :show-header="true"
          default-tab="programs"
          metric-category="Care Management"
          @close="closeDrawer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import abortable from '@/mixins/abortable';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import OperationsColumnHeader from '@/components/Care_Management/OperationsColumnHeader.vue';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import { downloadBlob } from '@/utils/workflowExports';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const ENDPOINT = '/api/care-management/complex-care-operations/';
const DRILL_PAGE = 100;

const easternToday = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const shiftDay = (dateStr, days) => {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export default {
  name: 'CareManagement_ComplexCare_Operations',
  components: { SimpleMultiSelect, ExportMenu, OperationsColumnHeader, Worklist_FilterBar, Patient_Drawer, FontAwesomeIcon },
  mixins: [abortable],
  data() {
    const today = easternToday();
    return {
      activeTab: 'activity',
      activePreset: 'mtd',
      dateFrom: `${today.slice(0, 7)}-01`,
      dateTo: today,
      selectedMetricIds: [],
      selectedMemberIds: [],
      managerIds: [],
      metricTouched: false,
      memberTouched: false,
      entityFilters: null,
      sortKey: null,
      sortDir: 'desc',
      loading: false,
      error: '',
      summary: null,
      exporting: false,
      options: { metrics: [], members: [], managers: [], can_view_all: false },
      rangePresets: [
        { value: 'lastweek', label: 'Last week' },
        { value: 'mtd', label: 'MTD' },
        { value: 'ytd', label: 'YTD' },
        { value: 'custom', label: 'Custom' },
      ],
      tabs: [
        { id: 'activity', label: 'Report one: Activity' },
        { id: 'outcomes', label: 'Report two: Outcomes' },
      ],
      drill: {
        active: false, loading: false, rowId: null, measureKey: null, rowLabel: '', colLabel: '', definition: '',
        rows: [], columns: [], total: 0, sortKey: null, sortDir: 'desc',
      },
      drawer: { open: false, entityId: null, patientIdentifier: null, patientName: '' },
    };
  },
  computed: {
    // The summary isn't paginated, so sort the loaded rows here instead of refetching.
    // Same rule as the backend: rows without a value go last, ties broken by row id.
    sortedRows() {
      const rows = this.summary?.rows || [];
      const key = this.sortKey;
      if (!key) return rows;
      const value = (row) => (key === 'label' ? row.label : row.cells?.[key]?.value);
      const sign = this.sortDir === 'desc' ? -1 : 1;
      const populated = rows.filter((r) => value(r) !== null && value(r) !== undefined);
      const empty = rows.filter((r) => value(r) === null || value(r) === undefined);
      populated.sort((a, b) => {
        const va = value(a);
        const vb = value(b);
        const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
        return sign * (cmp || String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
      });
      return [...populated, ...empty];
    },
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    contextKey() {
      return `${this.current_tenant_slug}:${this.current_user?.id || ''}`;
    },
    isToday() {
      return this.dateTo === easternToday();
    },
    metricOptions() {
      return (this.options.metrics || []).map((m) => ({ value: m.id, label: m.name }));
    },
    memberOptions() {
      return (this.options.members || []).map((m) => ({ value: m.id, label: m.name }));
    },
    managerOptions() {
      return (this.options.managers || []).map((m) => ({ value: m.id, label: m.name }));
    },
    unavailableColumns() {
      return (this.summary?.columns || []).filter((c) => c.availability === 'unavailable');
    },
    scopeParams() {
      return {
        date_from: this.dateFrom,
        date_to: this.dateTo,
        metric_ids: this.metricTouched ? [...this.selectedMetricIds] : null,
        member_ids: this.memberTouched ? [...this.selectedMemberIds] : null,
        manager_ids: this.managerIds.length ? [...this.managerIds] : null,
        entity_filters: this.entityFilters,
        view: this.activeTab,
      };
    },
  },
  watch: {
    contextKey: {
      immediate: true,
      handler() {
        this.fetchSummary();
      },
    },
  },
  methods: {
    selectTab(tabId) {
      if (this.activeTab === tabId) return;
      this.activeTab = tabId;
      this.sortKey = null;
      this.fetchSummary();
    },
    onCustomDate() {
      this.activePreset = 'custom';
      this.fetchSummary();
    },
    shiftRange(dir) {
      const span = Math.round((new Date(`${this.dateTo}T12:00:00Z`) - new Date(`${this.dateFrom}T12:00:00Z`)) / 86400000) + 1;
      this.activePreset = 'custom';
      this.dateFrom = shiftDay(this.dateFrom, dir * span);
      this.dateTo = shiftDay(this.dateTo, dir * span);
      if (this.dateTo > easternToday()) this.dateTo = easternToday();
      this.fetchSummary();
    },
    applyPreset(key) {
      this.activePreset = key;
      if (key === 'custom') return;
      const today = easternToday();
      this.dateTo = today;
      if (key === 'mtd') this.dateFrom = `${today.slice(0, 7)}-01`;
      if (key === 'ytd') this.dateFrom = `${today.slice(0, 4)}-01-01`;
      if (key === 'lastweek') {
        const weekday = new Date(`${today}T12:00:00Z`).getUTCDay();
        this.dateTo = shiftDay(today, -((weekday + 6) % 7) - 1);
        this.dateFrom = shiftDay(this.dateTo, -6);
      }
      this.fetchSummary();
    },
    setPrograms(ids) {
      this.metricTouched = true;
      this.selectedMetricIds = ids;
      this.fetchSummary();
    },
    setMembers(ids) {
      this.memberTouched = true;
      this.selectedMemberIds = ids;
      this.fetchSummary();
    },
    setManagers(ids) {
      this.managerIds = ids || [];
      this.memberTouched = false;
      this.fetchSummary();
    },
    setEntityFilters(payload) {
      const next = payload?.entity_filters ?? null;
      if (JSON.stringify(next) === JSON.stringify(this.entityFilters)) return;
      this.entityFilters = next;
      this.fetchSummary();
    },
    resetFilters() {
      this.metricTouched = false;
      this.memberTouched = false;
      this.managerIds = [];
      this.fetchSummary();
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
      } else {
        this.sortKey = key;
        this.sortDir = key === 'label' ? 'asc' : 'desc';
      }
    },
    sortMark(key) {
      if (this.sortKey !== key) return '';
      return this.sortDir === 'desc' ? ' ▼' : ' ▲';
    },
    headerStyle(align, disabled = false) {
      return {
        padding: '6px 10px',
        fontSize: '10.5px',
        fontWeight: 600,
        color: disabled ? '#A5A5A6' : '#6B7280',
        textAlign: align,
        position: 'sticky',
        top: 0,
        background: '#FFFFFF',
        zIndex: 1,
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      };
    },
    cellOf(row, col) {
      return (row.cells && row.cells[col.key]) || { value: null, drillable: false, count: 0 };
    },
    formatCell(cell, col) {
      if (cell == null || cell.value === null || cell.value === undefined) return '—';
      if (col.format === 'percent') {
        const pct = `${cell.value}%`;
        return cell.numerator != null && cell.denominator != null ? `${pct} (${cell.numerator}/${cell.denominator})` : pct;
      }
      if (col.format === 'currency') {
        return cell.value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
      }
      if (col.format === 'decimal') {
        return cell.value.toLocaleString(undefined, { maximumFractionDigits: 1 });
      }
      return typeof cell.value === 'number' ? cell.value.toLocaleString() : cell.value;
    },
    formatDrillVal(val, col) {
      if (val === null || val === undefined || val === '') return '—';
      if (col.format === 'datetime') {
        return new Date(val).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'short', timeStyle: 'short' });
      }
      return val;
    },
    async fetchSummary() {
      const signal = this._renewAbortSignal('summary');
      this.closeDrill();
      this.error = '';
      if (!this.current_tenant_slug || !this.current_user?.id) return;
      this.loading = true;
      try {
        const { data } = await this.$axios.post(ENDPOINT, {
          ...this.scopeParams,
          action: 'summary',
          sort_key: this.sortKey,
          sort_dir: this.sortDir,
        }, { signal });
        if (signal.aborted) return;
        this.summary = data;
        this.options = data.options || this.options;
        if (!this.metricTouched) this.selectedMetricIds = data.options?.default_metric_ids || [];
        if (!this.memberTouched) this.selectedMemberIds = (data.options?.members || []).map((m) => m.id);
      } catch (e) {
        if (this._isAborted(e)) return;
        this.error = 'Could not load Complex Care operations data. Please try again.';
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    },
    drillParams(offset) {
      return {
        ...this.scopeParams,
        action: 'drill',
        row_id: this.drill.rowId,
        measure_key: this.drill.measureKey,
        limit: DRILL_PAGE,
        offset,
        sort_key: this.drill.sortKey,
        sort_dir: this.drill.sortDir,
      };
    },
    async openDrill(row, col) {
      Object.assign(this.drill, {
        active: true, rowId: row.id, measureKey: col.key, rowLabel: row.label, colLabel: col.label,
        definition: col.definition || '', sortKey: null, sortDir: 'desc',
      });
      await this.fetchDrill();
    },
    async fetchDrill() {
      const signal = this._renewAbortSignal('drill');
      this.drill.loading = true;
      this.drill.rows = [];
      try {
        const { data } = await this.$axios.post(ENDPOINT, this.drillParams(0), { signal });
        if (signal.aborted) return;
        this.drill.rows = data?.rows || [];
        this.drill.columns = data?.columns || [];
        this.drill.total = data?.total || 0;
      } catch (e) {
        if (this._isAborted(e)) return;
        this.error = 'Could not load the records behind this number.';
      } finally {
        if (!signal.aborted) this.drill.loading = false;
      }
    },
    async loadMoreDrill() {
      const signal = this._renewAbortSignal('drill');
      const { data } = await this.$axios.post(ENDPOINT, this.drillParams(this.drill.rows.length), { signal });
      if (signal.aborted) return;
      this.drill.rows = [...this.drill.rows, ...(data?.rows || [])];
    },
    sortDrill(key) {
      if (this.drill.sortKey === key) {
        this.drill.sortDir = this.drill.sortDir === 'desc' ? 'asc' : 'desc';
      } else {
        this.drill.sortKey = key;
        this.drill.sortDir = 'desc';
      }
      this.fetchDrill();
    },
    drillSortMark(key) {
      if (this.drill.sortKey !== key) return '';
      return this.drill.sortDir === 'desc' ? ' ▼' : ' ▲';
    },
    closeDrill() {
      this.drill.active = false;
      this.drill.rows = [];
    },
    openPatient(row) {
      if (!row || !row.entity_id) return;
      this.drawer = {
        open: true,
        entityId: row.entity_id,
        patientIdentifier: row.patient_identifier,
        patientName: row.patient_name || '',
      };
    },
    closeDrawer() {
      this.drawer.open = false;
    },
    async exportReport(target, format) {
      if (target === 'drill' && !this.drill.active) return;
      this.exporting = true;
      try {
        const body = {
          ...this.scopeParams,
          action: 'export',
          export_target: target,
          format,
          sort_key: target === 'drill' ? this.drill.sortKey : this.sortKey,
          sort_dir: target === 'drill' ? this.drill.sortDir : this.sortDir,
        };
        if (target === 'drill') {
          body.row_id = this.drill.rowId;
          body.measure_key = this.drill.measureKey;
        }
        const { data } = await this.$axios.post(ENDPOINT, body, { responseType: 'blob' });
        // Demo Mode may have started while the export was generating.
        downloadBlob(new Blob([data]), `complex_care_${this.activeTab}_${target}_${this.dateFrom}_${this.dateTo}.${format}`);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
